//node modules
import React, {ReactElement, Suspense, useEffect, useMemo, useRef, useState} from 'react';
import {PagePermission, pageTree} from "./context/pageTree";
import PageLayout from "./layouts/pageLayout";
import {Route, Routes, useLocation, useNavigate} from "react-router-dom";
import {HelmetProvider} from "react-helmet-async";
import {AppStateProps, useAppState} from "./context/state";
import {onAuthStateChanged, signOut} from "firebase/auth";
import {Auth} from "./firebase/firebase";
import {useFbGetUser} from "./global/callback functions/autentication/fb_getUser";
import PageLoading from "./modules/Loading/PageLoading";
import momentTz from "moment-timezone";
import moment from 'moment'
import 'moment/locale/nb'
import {UserProps} from "./global/schemas/data/users/users";
import {
  BarElement,
  CategoryScale,
  Chart as ChartJS,
  Legend,
  LinearScale,
  LineElement,
  PointElement,
  Title,
  Tooltip,
} from 'chart.js';

moment.locale("nb");
momentTz.tz("Europe/Oslo");

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend
);

export default function App() {
  const {appState, setAppState} = useAppState();
  const [pageAuthed, setPageAuthed] = useState(false);
  const [processing, setProcessing] = useState(true);

  const router = useNavigate();
  const location = useLocation();
  const fbGetUser = useFbGetUser();


  //user validation, checks for admin in auth token
  const hasSetUpListener = useRef(false);
  useEffect(() => {
    if (!hasSetUpListener.current) {
      onAuthStateChanged(Auth, (user) => {
        if (user) {
          user.getIdTokenResult()
            .then(async (tokenResults) => {
              const userData = await fbGetUser() as UserProps;

              const isSupplier = Object.values(tokenResults.claims.suppliers || {}).some(Boolean)
              console.log(isSupplier, tokenResults.claims.suppliers)
              if (tokenResults.claims.admin) {
                setAppState((old) => ({
                  ...old,
                  user: {
                    ...userData,
                    permission: PagePermission.ADMIN
                  },
                  user_error: false,
                }));
                if (location.pathname === "/login") {
                  router("/");
                }
              } else if (tokenResults.claims.moderator) {
                setAppState((old) => ({
                  ...old,
                  user: {
                    ...userData,
                    permission: PagePermission.MODERATOR
                  },
                  user_error: false,
                }));
                if (location.pathname === "/login") {
                  router("/");
                }
              }
              else if (isSupplier) {
                setAppState((old) => ({
                  ...old,
                  user: {
                    ...userData,
                    permission: PagePermission.SUPPLIER
                  },
                  user_error: false,
                }));
                if (location.pathname === "/login") {
                  router("/supplier");
                }
              }
              else if (tokenResults.claims.distributer) {
                setAppState((old) => ({
                  ...old,
                  user: {
                    ...userData,
                    permission: PagePermission.DISTRIBUTER
                  },
                  user_error: false,
                }));
                if (location.pathname === "/login") {
                  router("/supplier");
                }
              } else {
                throw new Error("not a valid user");
              }

              setPageAuthed(true)
              setProcessing(false);
            })
            .catch(() => {
              setPageAuthed(false);
              setAppState((old) => ({
                ...old,
                user: null,
                user_error: "Not a valid user",
                processing: false,
              }));
              void signOut(Auth);
            });
        } else {
          if (location.pathname !== "/login") {
            router("/login");
          }
          setAppState((old) => ({
            ...old,
            user: null,
            processing: false,
          }));
          setPageAuthed(false);
          setProcessing(false);
        }
      });

      hasSetUpListener.current = true;
    }
  }, [fbGetUser, location.pathname, router, setAppState]);

  const pages = useMemo(() => {
    return pageTree.reduce((pageList: ReactElement[], page, i) => {
      const route = getPageRoutes(!pageAuthed, page, `${i}`, appState);

      if (Array.isArray(route)) {
        pageList = pageList.concat(route);
      } else if (route) {
        pageList.push(route);
      }

      return pageList;
    }, [])
  }, [appState, pageAuthed])

  console.log(pageAuthed)
  if (!processing) {
    return (
      <>
        <HelmetProvider>
          {pageAuthed ? (
            <Suspense fallback={<PageLoading/>}>
              <PageLayout pageLinks={pageTree}>
                <Routes>
                  {pages}
                </Routes>
              </PageLayout>
            </Suspense>
          ) : (
            <Routes>
              {pages}
            </Routes>
          )}
        </HelmetProvider>
      </>
    );
  } else {
    return <PageLoading/>
  }
}

function getPageRoutes(isAuthed: boolean, page: any, pageSection?: string, userClaims?: AppStateProps) {
  if (page?.subPages) {
    return page.subPages.reduce((pageList: ReactElement[], page: any, i: number) => {
      const route = getPageRoutes(isAuthed, page, `-${page.title ?? page.page}-${i}`.replaceAll(' ', '-'), userClaims);

      if (Array.isArray(route)) {
        pageList = pageList.concat(route);
      } else if (route) {
        pageList.push(route);
      }

      return pageList;
    }, [])
  }

  if (page.needAuth === false) {
    return <Route path={page.link} element={<page.element/>} key={"page" + (pageSection ?? page?.page)}/>;
  }

  if (typeof userClaims?.user?.permission === "number" && userClaims?.user?.permission <= page.needAuth) {
    return <Route path={page.link} element={<page.element/>} key={"page" + (pageSection ?? page?.page)}/>;
  }

  return null;
}
